/**
 * The funnel's answers across the Stripe round trip.
 *
 * Checkout is another site, and coming back — cancelled, or with the Back
 * button — reloads the funnel from nothing: the paywall then showed "your
 * child" with no avatar, and there was no way back to what the parent had
 * built. So the paywall's inputs are snapshotted the moment the parent heads
 * to Stripe, and a fresh load within the hour resumes on the paywall with them.
 * The avatar itself is not stored (three PNGs would crowd sessionStorage);
 * the stage re-captures it from the saved look in a second or two.
 *
 * sessionStorage, not localStorage: one tab, one visit, gone when it closes.
 * Nothing here is the photo — only the look derived from it and the answers.
 */
import type { PlanId } from './billing';
import type { FunnelLook } from './avatarLook';

export interface Snapshot {
  answers: Record<string, string | string[]>;
  name: string;
  look: FunnelLook;
  plan: PlanId;
  testMode: boolean;
  at: number;
}

const KEY = 'ct.resume';
const MAX_AGE_MS = 60 * 60 * 1000;

export function saveSnapshot(s: Omit<Snapshot, 'at'>): void {
  try { sessionStorage.setItem(KEY, JSON.stringify({ ...s, at: Date.now() })); } catch { /* private mode */ }
}

export function loadSnapshot(): Snapshot | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Snapshot;
    if (!s || typeof s.at !== 'number' || Date.now() - s.at > MAX_AGE_MS) return null;
    return s;
  } catch {
    return null;
  }
}

export function clearSnapshot(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}
